/* TRC $99 Hero Slider
   Crossfading autoplay hero. Autoplay pauses on hover (hover-capable devices
   only), keyboard focus, the pause/play control, hidden tabs and
   prefers-reduced-motion, and is disabled entirely in the theme editor
   (data-autoplay="false"). Mobile navigates by swipe + indicator bars. */

if (!customElements.get('trc-hero-slider')) {
  class TrcHeroSlider extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('.trc99-slide'));
      if (this.slides.length === 0) return;

      this.bars = Array.from(this.querySelectorAll('.trc99-bar'));
      this.countEl = this.querySelector('[data-trc99-count]');
      this.prevBtn = this.querySelector('.trc99-arrow--prev');
      this.nextBtn = this.querySelector('.trc99-arrow--next');
      this.playBtn = this.querySelector('.trc99-playpause');
      this.interval = (parseInt(this.dataset.interval, 10) || 7) * 1000;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.hoverCapable = window.matchMedia('(hover: hover) and (pointer: fine)');
      this.autoplay = this.dataset.autoplay === 'true' && this.slides.length > 1;
      this.hovered = false;
      this.focused = false;
      this.userPaused = false;
      this.touchStartX = null;
      this.touchStartY = null;
      this.timer = null;

      /* one controller tears down every listener on disconnect, so a DOM move
         (theme editor section reorder) cannot stack duplicate handlers */
      this.controller = new AbortController();
      const signal = this.controller.signal;

      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => this.goTo(this.index - 1), { signal });
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => this.goTo(this.index + 1), { signal });
      }
      this.bars.forEach((bar, i) => bar.addEventListener('click', () => this.goTo(i), { signal }));

      if (this.playBtn) {
        if (this.autoplay) {
          this.playBtn.addEventListener(
            'click',
            () => {
              this.userPaused = !this.userPaused;
              this.sync();
            },
            { signal }
          );
        } else {
          this.playBtn.hidden = true;
        }
      }

      this.addEventListener(
        'mouseenter',
        () => {
          if (!this.hoverCapable.matches) return;
          this.hovered = true;
          this.sync();
        },
        { signal }
      );
      this.addEventListener(
        'mouseleave',
        () => {
          this.hovered = false;
          this.sync();
        },
        { signal }
      );

      this.addEventListener(
        'focusin',
        (event) => {
          if (this.isFocusVisible(event.target)) {
            this.focused = true;
            this.sync();
          }
        },
        { signal }
      );
      this.addEventListener(
        'focusout',
        (event) => {
          if (this.focused && !this.contains(event.relatedTarget)) {
            this.focused = false;
            this.sync();
          }
        },
        { signal }
      );

      this.addEventListener(
        'keydown',
        (event) => {
          if (event.key === 'ArrowRight') {
            event.preventDefault();
            this.goTo(this.index + 1);
          }
          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            this.goTo(this.index - 1);
          }
        },
        { signal }
      );

      this.addEventListener(
        'touchstart',
        (event) => {
          // the rug lineup is its own horizontal scroller - don't treat
          // scrolling it as a slide swipe
          if (event.target.closest('.trc99-lineup')) return;
          const touch = event.changedTouches[0];
          this.touchStartX = touch.clientX;
          this.touchStartY = touch.clientY;
        },
        { signal, passive: true }
      );
      this.addEventListener(
        'touchend',
        (event) => {
          if (this.touchStartX === null) return;
          const touch = event.changedTouches[0];
          const deltaX = touch.clientX - this.touchStartX;
          const deltaY = touch.clientY - this.touchStartY;
          this.touchStartX = null;
          this.touchStartY = null;
          if (Math.abs(deltaX) > 48 && Math.abs(deltaX) > Math.abs(deltaY) * 1.5) {
            this.goTo(this.index + (deltaX < 0 ? 1 : -1));
          }
        },
        { signal, passive: true }
      );

      document.addEventListener('visibilitychange', () => this.sync(), { signal });
      this.reducedMotion.addEventListener('change', () => this.sync(), { signal });

      if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener(
          'shopify:block:select',
          (event) => {
            const slideIndex = this.slides.indexOf(event.target);
            if (slideIndex >= 0) this.goTo(slideIndex);
          },
          { signal }
        );
      }

      const initial = this.slides.findIndex((slide) => slide.classList.contains('is-active'));
      this.activate(initial >= 0 ? initial : 0);
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      if (this.controller) this.controller.abort();
    }

    isFocusVisible(target) {
      try {
        return target.matches(':focus-visible');
      } catch (error) {
        // no :focus-visible support - pausing on any focus beats never pausing
        return true;
      }
    }

    get playing() {
      return (
        this.autoplay &&
        !this.userPaused &&
        !this.hovered &&
        !this.focused &&
        !document.hidden &&
        !this.reducedMotion.matches
      );
    }

    goTo(rawIndex) {
      const count = this.slides.length;
      this.activate(((rawIndex % count) + count) % count);
    }

    activate(index) {
      this.index = index;
      this.slides.forEach((slide, i) => {
        slide.classList.toggle('is-active', i === index);
        slide.setAttribute('aria-hidden', i === index ? 'false' : 'true');
      });
      this.setAttribute('data-active-type', this.slides[index].dataset.slideType || '');
      if (this.countEl) {
        this.countEl.textContent =
          String(index + 1).padStart(2, '0') + ' / ' + String(this.slides.length).padStart(2, '0');
      }
      this.sync();
    }

    /* Re-evaluate bars, pause control and timer after any state change
       (navigation, hover, focus, pause toggle, tab visibility, reduced
       motion). The current bar's fill restarts via an animation reset. */
    sync() {
      this.bars.forEach((bar, i) => {
        const fill = bar.querySelector('.trc99-bar__fill');
        bar.classList.remove('is-running', 'is-done');
        if (i === this.index) {
          bar.setAttribute('aria-current', 'true');
        } else {
          bar.removeAttribute('aria-current');
        }
        if (i < this.index) {
          bar.classList.add('is-done');
        } else if (i === this.index) {
          if (this.playing && fill) {
            fill.style.animation = 'none';
            void fill.offsetWidth;
            fill.style.animation = '';
            bar.classList.add('is-running');
          } else {
            bar.classList.add('is-done');
          }
        }
      });

      if (this.playBtn && this.autoplay) {
        this.playBtn.classList.toggle('is-paused', this.userPaused);
        const label = this.userPaused ? this.playBtn.dataset.labelPlay : this.playBtn.dataset.labelPause;
        if (label) this.playBtn.setAttribute('aria-label', label);
        this.playBtn.setAttribute('aria-pressed', this.userPaused ? 'true' : 'false');
      }

      clearTimeout(this.timer);
      if (this.playing) {
        this.timer = setTimeout(() => this.goTo(this.index + 1), this.interval);
      }
    }
  }

  customElements.define('trc-hero-slider', TrcHeroSlider);
}